import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { useReportUser } from '../hooks/useFriends'
import { REPORT_REASONS, type ReportReasonValue } from '../types'

interface ReportUserModalProps {
  readonly userId: string
  readonly username: string | null
  readonly onClose: () => void
}

/** Signalement d'un profil (spécifications § 7.3). Le motif est imposé par l'API. */
export function ReportUserModal({ userId, username, onClose }: ReportUserModalProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<ReportReasonValue>(REPORT_REASONS[0])
  const [details, setDetails] = useState('')
  const report = useReportUser()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    try {
      await report.mutateAsync({
        user_id: userId,
        reason,
        details: details.trim() === '' ? undefined : details.trim(),
      })
      onClose()
    } catch {
      // L'état d'erreur de la mutation suffit à l'affichage.
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="social-report-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 id="social-report-title" className="mb-1 text-lg font-semibold text-neutral-900">
          {t('social.moderation.report_title')}
        </h2>
        <p className="mb-5 text-sm text-on-surface-variant">
          {t('social.moderation.report_subtitle', { username: username ?? '' })}
        </p>

        {report.isError && (
          <Alert variant="error" className="mb-4">
            {t('social.moderation.report_error')}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="social-report-reason"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              {t('social.moderation.reason')}
            </label>
            <select
              id="social-report-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as ReportReasonValue)}
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {REPORT_REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`social.moderation.reasons.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="social-report-details"
              className="mb-1 block text-sm font-medium text-neutral-700"
            >
              {t('social.moderation.details')}
            </label>
            <textarea
              id="social-report-details"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              rows={3}
              maxLength={1000}
              className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={report.isPending}>
              {t('social.moderation.report_submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
